import React, { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getNotificationsSocket } from '@services/websocket/socket'
import { fetchEmailMessages } from '@store/actions/email.action'
import { resetEmailState } from '@store/reducers/email.reducer'
import { isEmailBackendEnabled } from '@services/api/email.api'
import {
  NotificationCategory,
  NotificationTriggerType,
} from '@dataTypes/notification.types'

interface NotificationCreatedEvent {
  notification?: {
    clinic_id?: string | null
    category?: string | null
    type?: string | null
  }
}

const EMAIL_REFRESH_TYPES = new Set<string>([
  NotificationTriggerType.EMAIL_NEW_RECEIVED,
  NotificationTriggerType.EMAIL_REPLY_RECEIVED,
])

const EMAIL_POLL_INTERVAL_MS = 30000

const EmailSocketListener: React.FC = () => {
  const dispatch = useAppDispatch()
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const selectedClinicId = selectedClinic?.clinic_id

  useEffect(() => {
    const refreshForClinic = (
      eventClinicId: string | null | undefined,
    ): void => {
      if (!selectedClinic) return
      if (eventClinicId && eventClinicId !== selectedClinic.clinic_id) return
      void dispatch(fetchEmailMessages({ silent: true }))
    }

    const handleNotificationCreated = (
      payload: NotificationCreatedEvent,
    ): void => {
      const notification = payload?.notification
      if (!notification) return
      if (notification.category !== NotificationCategory.EMAIL) return
      if (!notification.type || !EMAIL_REFRESH_TYPES.has(notification.type)) {
        return
      }
      refreshForClinic(notification.clinic_id)
    }

    const socket = getNotificationsSocket()
    if (!socket) return

    socket.on('notification.created', handleNotificationCreated)
    return () => {
      socket.off('notification.created', handleNotificationCreated)
    }
  }, [dispatch, selectedClinic])

  // Clear prior clinic inbox, then poll Gmail-backed list app-wide (list API syncs on fetch).
  useEffect(() => {
    if (!isEmailBackendEnabled() || !selectedClinicId) return

    dispatch(resetEmailState())

    const poll = (): void => {
      if (document.visibilityState !== 'visible') return
      void dispatch(fetchEmailMessages({ silent: true }))
    }

    poll()
    const intervalId = window.setInterval(poll, EMAIL_POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', poll)

    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', poll)
    }
  }, [dispatch, selectedClinicId])

  return null
}

export default EmailSocketListener
